import { ChangeEvent } from 'react';
import { scalar } from '@magical-forms/react-next';
import { array, object } from '@magical-forms/react-next';

function validationToFieldProps<
  Obj extends {
    validity: 'valid' | 'invalid';
    error?: string;
    touched: boolean;
  }
>({
  error,
  validity,
  touched,
  ...obj
}: Obj): Omit<Obj, 'error' | 'validity' | 'touched'> & {
  invalidMessage?: string;
} {
  if (touched) {
    return {
      invalidMessage: error,
      ...obj,
    };
  }
  return obj;
}

export const multiselect = scalar({
  props: (field) => ({
    ...validationToFieldProps(field),
    blur: () => {
      field.onBlur();
    },
    onBlur: undefined,
  }),
  initialValue: (input: string[] | undefined) => input || [],
});

export const text = scalar({
  props: ({ onChange, ...field }) => ({
    ...validationToFieldProps(field),
    onChange(event: ChangeEvent<HTMLInputElement>) {
      onChange(event.target.value);
    },
    blur: () => {
      field.onBlur();
    },
    onBlur: undefined,
  }),
  initialValue: (input: string | undefined | null) => input || '',
});

export const multilineText = scalar({
  props: ({ onChange, ...field }) => ({
    ...validationToFieldProps(field),
    onChange(event: ChangeEvent<HTMLTextAreaElement>) {
      onChange(event.target.value);
    },
    blur: () => {
      field.onBlur();
    },
    onBlur: undefined,
  }),
  initialValue: (input: string | undefined | null) => input || '',
});

export const email = scalar({
  props: ({ onChange, ...field }) => ({
    ...validationToFieldProps(field),
    inputMode: 'email' as const,
    onChange(event: ChangeEvent<HTMLInputElement>) {
      onChange(event.target.value);
    },
    blur: () => {
      field.onBlur();
    },
    onBlur: undefined,
  }),
  initialValue: (input: string | undefined | null) => input || '',
});

// export const number = scalar({
//   props: ({ onChange, validity, error, value, ...field }) => ({
//     ...field,
//     value: value === undefined ? '' : '' + value,
//     invalidMessage: error,
//     onChange(event: ChangeEvent<HTMLInputElement>) {
//       onChange(
//         event.target.value === '' ? undefined : Number(event.target.value)
//       );
//     },
//   }),
//   initialValue: (input: number | undefined) => input,
// });

export const currency = scalar({
  props: (field) => ({
    ...validationToFieldProps(field),
    blur: () => {
      field.onBlur();
    },
    onBlur: undefined,
  }),
  initialValue: (input: string | number | undefined | null) => input || '',
});

export const duration = scalar({
  props: ({ onChange, ...field }) => ({
    ...validationToFieldProps(field),
    onChange(value?: string | number) {
      onChange(value !== undefined ? value : '');
    },
    blur: () => {
      field.onBlur();
    },
    onBlur: undefined,
  }),
  initialValue: (input: string | number | undefined | null) => input || '',
});

export const boolean = scalar({
  props: (field) => ({
    ...validationToFieldProps(field),
    blur: () => {
      field.onBlur();
    },
    onBlur: undefined,
  }),
  initialValue: (input: boolean | undefined | null) => input || false,
});

export const string = scalar({
  props: (field) => ({
    ...validationToFieldProps(field),
    blur: () => {
      field.onBlur();
    },
    onBlur: undefined,
  }),
  initialValue: (input: string | undefined | null) => input ?? undefined,
});

// TODO: remove these dates from here and import from a non jsx rendering date orientated package
type ISODate = string & { ___ISODateString: true };

type ISODateRange = { start: ISODate; end: ISODate };

export const date = scalar({
  props: (input) => ({
    ...validationToFieldProps(input),
    onClear() {
      input.onChange(undefined);
    },
    blur: () => {
      input.onBlur();
    },
    onBlur: undefined,
  }),
  initialValue: (input: ISODate | undefined | null) => input ?? undefined,
});

export const dateRange = scalar({
  props: (field) => ({
    ...validationToFieldProps(field),
    blur: () => {
      field.onBlur();
    },
    onBlur: undefined,
  }),
  initialValue: (input: ISODateRange | undefined | null) => input ?? undefined,
});

export const empty = scalar({
  props: () => undefined,
  initialValue: (input: undefined) => input,
});

export const select = <Value extends string>() =>
  scalar({
    props: (field) => ({
      ...validationToFieldProps(field),
      blur: () => {
        field.onBlur();
      },
      onBlur: undefined,
    }),
    initialValue: (input: Value | undefined | null) => input ?? undefined,
  });

export const autocomplete = <
  Option extends { label: string; value: string }
>() =>
  scalar({
    props: (field) => ({
      ...validationToFieldProps(field),
      blur: () => {
        field.onBlur();
      },
      onBlur: undefined,
    }),
    initialValue: (input: Option | undefined | null) => input ?? null,
  });

const field = {
  object,
  array,
  autocomplete,
  select,
  empty,
  dateRange,
  date,
  string,
  boolean,
  duration,
  currency,
  email,
  text,
  multilineText,
  multiselect,
};

// export as composites
export { scalar, object, array, field };
